import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Card,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Switch,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
} from '@/components/ui';
import {
  useFieldRequirements,
  useFieldRequirementActions,
  type FieldRequirement,
} from '@/lib/field-requirements-store';
// P1 (ajuste de review): a config do SLA "prazo pra iniciar" mora aqui em
// Configurações (saiu do quadro de Projetos pendentes). Gated por PROJETOS_V2.
import { useAuth } from '@/contexts/AuthContext';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { useSlaIniciar, useSetSlaIniciar, useDiasSemAtualizar, useSetDiasSemAtualizar } from '@/lib/projetos-store';
// R4 L2: Metas + Contas do Financeiro moram AQUI (Configurações global, seção
// "Financeiro") — a página própria /admin/financeiro/configuracoes duplicava
// o "Configurações" e foi removida.
import { FinanceiroContasConfigCard } from './financeiro/FinanceiroContasConfigCard';
import { FinanceiroMetasConfigCard } from './financeiro/FinanceiroMetasConfigCard';
import { FinanceiroFinanciamentoConfigCard } from './financeiro/FinanceiroFinanciamentoConfigCard';
import { FinanceiroRelatoriosConfigCard } from './financeiro/FinanceiroRelatoriosConfigCard';
// F2 (14/08): inventário completo por régua de etiqueta — fonte única.
import { SECOES_INVENTARIO, contarCamposDoRapido } from '@/lib/clientes/inventario-campos';
// I-2 — limites do rate limit de login + destrave (Configurações › Sistema).
import { SegurancaLoginConfigCard } from './SegurancaLoginConfigCard';

/**
 * /admin/configuracoes — "Regras gerais do sistema".
 *
 * FASE-C (redesign): a tela deixou de ser três blocos soltos sob o h1
 * genérico "Configurações". Agora tem PageHeader próprio e três seções
 * nomeadas, na ordem em que o admin pensa:
 *   1. Preenchimento obrigatório — o que a equipe é obrigada a informar
 *      (cadastro de cliente + card do funil).
 *   2. Expediente e prazos — horário de funcionamento/feriados + o prazo
 *      pra iniciar um projeto (as duas configs são "tempo", ficavam
 *      separadas sem motivo).
 *   3. Financeiro — contas, metas, financiamento e relatórios.
 * As âncoras `#sistema` e `#financeiro` foram preservadas (o menu admin
 * linka /admin/configuracoes#financeiro).
 *
 * Regras de dado (inalteradas):
 *   - Cliente é `_global`: a regra vale pra todos os fluxos de trabalho.
 *   - Card é por fluxo de trabalho (hoje só existe o Comercial).
 *   - Campos novos nascem OPCIONAIS (sem entry no banco = não obrigatório).
 *   - Campo `fixed=1` = obrigatório por regra de negócio, Switch travado.
 *
 * Pra acrescentar novo campo configurável: adicione em CONFIG_FIELDS +
 * garanta que o helper backend olha o field_name correspondente. Sem
 * migration nova.
 */

interface ConfigField {
  fieldName: string;
  label: string;
  hint?: string;
}

const CONFIG_FIELDS: { card: ConfigField[]; client: ConfigField[] } = {
  card: [
    { fieldName: 'titulo', label: 'Título do card' },
    { fieldName: 'clienteId', label: 'Cliente', hint: 'Vínculo com cadastro de clientes' },
    { fieldName: 'produto', label: 'Produto' },
    { fieldName: 'servico', label: 'Serviço' },
    { fieldName: 'valor', label: 'Valor (R$)' },
    { fieldName: 'responsavelId', label: 'Responsável' },
    { fieldName: 'proximaAcao', label: 'Próxima ação' },
    { fieldName: 'proximaAcaoDataIso', label: 'Data da próxima ação' },
    { fieldName: 'observacoes', label: 'Observações' },
    { fieldName: 'origem', label: 'Origem do lead' },
  ],
  // 13/08 (mig 468): vocabulário do Clientes v2 — estes nomes casam com o que
  // o POST /clientes valida (assertRequiredFields). `documento` = CNPJ OU CPF:
  // o admin exige "um documento", não o tipo.
  client: [
    { fieldName: 'razaoSocial', label: 'Razão social / Nome' },
    { fieldName: 'documento', label: 'CNPJ ou CPF', hint: 'Sem marcar aqui, ele continua exigido só no Ganho' },
    { fieldName: 'email', label: 'E-mail' },
    { fieldName: 'telefone', label: 'Telefone' },
    { fieldName: 'numUnidades', label: 'Nº de unidades' },
  ],
};

/**
 * Fluxos de trabalho que têm regras próprias de card. Hoje só existe o
 * Comercial — por isso o seletor abaixo só aparece quando há mais de um
 * (um botão sozinho que não escolhe nada é enfeite).
 */
const WORKFLOWS: Array<{ slug: string; label: string }> = [
  { slug: 'comercial', label: 'Comercial' },
];

/** Seção nomeada da tela — dá hierarquia e serve de âncora de deep-link. */
function Secao({
  id,
  titulo,
  descricao,
  children,
  className,
}: {
  id?: string;
  titulo: string;
  descricao: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`min-w-0 scroll-mt-4 ${className ?? ''}`}>
      <h2 className="font-display text-2xl tracking-tight text-ink-1 dark:text-ink-dark-1">
        {titulo}
      </h2>
      <p className="mb-4 mt-1 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">{descricao}</p>
      {children}
    </section>
  );
}

function findEntry(
  rows: FieldRequirement[],
  workflowSlug: string,
  entityType: 'card' | 'client',
  fieldName: string,
  etiqueta = ''
): FieldRequirement | null {
  const specific = rows.find(
    (r) =>
      r.workflowSlug === workflowSlug &&
      r.entityType === entityType &&
      r.fieldName === fieldName &&
      (r.etiqueta ?? '') === etiqueta
  );
  if (specific) return specific;
  // Fallback workflow→_global (cards). Filtra a MESMA etiqueta: sem isso, a
  // seção "Cliente" mostraria ligado o que só está exigido na régua Geral.
  const global = rows.find(
    (r) =>
      r.workflowSlug === '_global' &&
      r.entityType === entityType &&
      r.fieldName === fieldName &&
      (r.etiqueta ?? '') === etiqueta
  );
  return global ?? null;
}

interface RowConfig extends ConfigField {
  entry: FieldRequirement | null;
}

interface RequirementTableProps {
  title: string;
  subtitle: string;
  loading: boolean;
  rows: RowConfig[];
  entityType: 'card' | 'client';
  workflowForEntry: string;
  /** F2: '' = régua global; senão, etiqueta exata (seções por etiqueta). */
  etiqueta?: string;
  pendingKey: string | null;
  onToggle: (
    entityType: 'card' | 'client',
    fieldName: string,
    target: boolean,
    workflow: string,
    etiqueta?: string
  ) => void;
}

function RequirementTable({
  title,
  subtitle,
  loading,
  rows,
  entityType,
  workflowForEntry,
  etiqueta = '',
  pendingKey,
  onToggle,
}: RequirementTableProps) {
  return (
    <div className="min-w-0 p-5">
      {title && (
        <h3 className="mb-1 font-display text-xl text-ink-1 dark:text-ink-dark-1">{title}</h3>
      )}
      <p className="mb-4 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">{subtitle}</p>
      {loading ? (
        <p className="py-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhum campo configurável"
          description="Não há campos disponíveis pra tornar obrigatórios nesta lista."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campo</TableHead>
              <TableHead>Obrigatório</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isRequired = row.entry?.required ?? false;
              const isFixed = row.entry?.fixed ?? false;
              const rowKey = `${workflowForEntry}:${entityType}:${etiqueta}:${row.fieldName}`;
              const isPending = pendingKey === rowKey;
              return (
                <TableRow key={row.fieldName} data-testid={etiqueta ? `req-row-${entityType}-${etiqueta}-${row.fieldName}` : `req-row-${entityType}-${row.fieldName}`}>
                  <TableCell>
                    <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                      {row.label}
                    </div>
                    {row.hint && (
                      <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                        {row.hint}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <Switch
                        checked={isRequired}
                        disabled={isFixed || isPending}
                        onChange={(e) =>
                          onToggle(entityType, row.fieldName, e.target.checked, workflowForEntry, etiqueta)
                        }
                        aria-label={`Tornar ${row.label} ${isRequired ? 'opcional' : 'obrigatório'}`}
                        data-testid={etiqueta ? `req-toggle-${entityType}-${etiqueta}-${row.fieldName}` : `req-toggle-${entityType}-${row.fieldName}`}
                      />
                      {isFixed && (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="neutral">Sempre obrigatório</Badge>
                          <HelpTip texto="Este campo é exigido por regra de negócio: sem ele o cadastro não funciona. Por isso não dá pra desmarcar." />
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Config do módulo Projetos — SLA "prazo pra iniciar" (P1, Parte 2).
 * Renderizado só com a flag PROJETOS_V2_ENABLED ON (gate no pai, pra os hooks
 * não rodarem com a flag OFF). Editável só por administrativo (ou super-admin)
 * — o PUT é gated no backend de qualquer forma. O quadro de Projetos pendentes
 * só lista/age; a config vive aqui.
 */
function ProjetoConfigCard() {
  const { user } = useAuth();
  const podeEditar = !!user?.isAdmin || user?.profile?.slug === 'administrativo';
  const slaQuery = useSlaIniciar();
  const setSla = useSetSlaIniciar();
  const slaDias = slaQuery.data?.dias ?? null;

  const [valor, setValor] = useState('');
  useEffect(() => {
    if (slaDias !== null) setValor(String(slaDias));
  }, [slaDias]);

  function salvar() {
    const n = Number.parseInt(valor, 10);
    if (!Number.isFinite(n) || n < 1 || n > 365) return;
    setSla.mutate(n);
  }

  const dirty = String(slaDias ?? '') !== valor.trim();

  return (
    <Card className="mb-6 min-w-0 p-5" data-testid="projeto-sla-config">
      <h3 className="mb-1 font-display text-xl text-ink-1 dark:text-ink-dark-1">
        Prazo pra iniciar um projeto
      </h3>
      <p className="mb-4 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">
        Quantos dias a equipe tem pra tirar um projeto do estado pendente. Passou do prazo sem
        iniciar, o item fica em destaque no quadro de <strong>Projetos pendentes</strong>.
      </p>
      {!podeEditar ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2">
          Hoje o prazo é de <strong>{slaDias ?? '—'}</strong> dias corridos. Só o administrativo
          pode alterar.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0">
            <Label htmlFor="sla-dias">Prazo pra iniciar (dias corridos)</Label>
            <Input
              id="sla-dias"
              type="number"
              min="1"
              max="365"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="w-28 max-w-full"
              data-testid="sla-input"
            />
          </div>
          <Button onClick={salvar} disabled={!dirty || setSla.isPending} loading={setSla.isPending}>
            Salvar prazo
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * CRONOGRAMA-MSP item 11 (09/10/2026): o limite do selo "Cronograma sem atualizar" na lista de Projetos. Nasce 7 dias,
 * já aplicado (régua da casa: configuração que espera o dono é entrega pela metade). Mesmo porteiro do prazo acima.
 */
function CronogramaSemAtualizarCard() {
  const { user } = useAuth();
  const podeEditar = !!user?.isAdmin || user?.profile?.slug === 'administrativo';
  const query = useDiasSemAtualizar();
  const salvarDias = useSetDiasSemAtualizar();
  const dias = query.data?.dias ?? null;
  const [valor, setValor] = useState('');
  useEffect(() => {
    if (dias !== null) setValor(String(dias));
  }, [dias]);
  const n = Number.parseInt(valor, 10);
  const valido = Number.isFinite(n) && n >= 1 && n <= 365;
  const dirty = String(dias ?? '') !== valor.trim();
  return (
    <Card className="mb-6 min-w-0 p-5" data-testid="cronograma-sem-atualizar-config">
      <h3 className="mb-1 font-display text-xl text-ink-1 dark:text-ink-dark-1">Cronograma sem atualizar</h3>
      <p className="mb-4 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">
        Obra em andamento cujo arquivo do MS Project não é reimportado há mais destes dias ganha o selo{' '}
        <strong>Cronograma sem atualizar</strong> na lista de Projetos.
      </p>
      {!podeEditar ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2">
          Hoje o limite é de <strong>{dias ?? '—'}</strong> dias corridos. Só o administrativo pode alterar.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0">
            <Label htmlFor="dias-sem-atualizar">Limite (dias corridos)</Label>
            <Input
              id="dias-sem-atualizar"
              type="number"
              min="1"
              max="365"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="w-28 max-w-full"
              aria-invalid={!valido}
              data-testid="dias-sem-atualizar-input"
            />
            {!valido && valor.trim() !== '' && (
              <p className="mt-1 text-xs text-red-deep dark:text-red-glow" role="alert">
                Informe um número de dias entre 1 e 365.
              </p>
            )}
          </div>
          <Button onClick={() => valido && salvarDias.mutate(n)} disabled={!dirty || !valido || salvarDias.isPending} loading={salvarDias.isPending}>
            Salvar limite
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Bloco D — card de descoberta pra a config global de Horário de funcionamento
 * (expediente + feriados), que mora na própria tela /configuracoes/horario-funcionamento.
 */
function HorarioFuncionamentoConfigCard() {
  const navigate = useNavigate();
  return (
    <Card className="mb-6 min-w-0 p-5" data-testid="horario-funcionamento-config">
      <div className="mb-1 flex flex-wrap items-center gap-1">
        <h3 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
          Horário de funcionamento
        </h3>
        <HelpTip texto="Os dias e horários em que a empresa está aberta, mais os feriados. Vale pro sistema inteiro: é daqui que sai a conta de dias úteis, como o prazo de entrega de um orçamento." />
      </div>
      <p className="mb-4 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">
        Defina o expediente da semana e os feriados (nacionais, estaduais de SP e municipais de
        Paulínia). Esses dados valem pro sistema inteiro e são a base do cálculo de dias úteis.
      </p>
      <Button
        variant="outline"
        onClick={() => navigate('/admin/configuracoes/horario-funcionamento')}
        data-testid="abrir-horario-funcionamento"
      >
        Abrir horário e feriados
      </Button>
    </Card>
  );
}

export function AdminConfigPage() {
  const projetosV2 = useMyFeatureFlag('PROJETOS_V2_ENABLED');
  const [workflowSlug, setWorkflowSlug] = useState('comercial');
  // R4 L2: deep-link pra seção (ex. /admin/configuracoes#financeiro vindo do
  // grupo Integrações) — rola até a âncora depois do primeiro render.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);
  const { data: cardRows = [], isLoading: loadingCard } = useFieldRequirements({
    entityType: 'card',
  });
  const { data: clientRows = [], isLoading: loadingClient } = useFieldRequirements({
    entityType: 'client',
  });
  const { upsert, pending } = useFieldRequirementActions();
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (
    entityType: 'card' | 'client',
    fieldName: string,
    targetRequired: boolean,
    workflowForEntry: string,
    etiqueta = ''
  ) => {
    setError(null);
    try {
      await upsert({
        workflowSlug: workflowForEntry,
        entityType,
        etiqueta,
        fieldName,
        required: targetRequired,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      setError(`Não foi possível atualizar "${fieldName}": ${msg}`);
    }
  };

  const cardConfig: RowConfig[] = useMemo(
    () =>
      CONFIG_FIELDS.card.map((f) => ({
        ...f,
        entry: findEntry(cardRows, workflowSlug, 'card', f.fieldName),
      })),
    [cardRows, workflowSlug]
  );

  // F2: quantos campos o Cadastro rápido do funil vai mostrar com a régua
  // atual (união global ∪ Cliente) — cada exigência entra no fluxo do
  // vendedor, e sem o número o admin só descobre usando.
  const camposDoRapido = useMemo(() => contarCamposDoRapido(clientRows), [clientRows]);

  const fluxoAtual = WORKFLOWS.find((w) => w.slug === workflowSlug)?.label ?? 'Comercial';

  return (
    <div className="min-w-0">
      <PageHeader
        crumb="Configurações · Sistema"
        title="Regras gerais do sistema"
        subtitle="O que a equipe é obrigada a preencher, o expediente da empresa e os parâmetros do Financeiro."
      />

      {error && (
        <Alert variant="danger" rail className="mb-4">
          {error}
        </Alert>
      )}

      <Secao
        titulo="Preenchimento obrigatório"
        descricao="Por padrão quase tudo é opcional. Marque aqui o que sua equipe precisa informar sem falta. A mudança vale na próxima vez que alguém abrir o formulário (leva até 5 minutos pra chegar em todo mundo)."
      >
        <Alert variant="info" rail className="mb-4">
          Alguns campos aparecem como <strong>Sempre obrigatório</strong> e não podem ser
          desligados — sem eles o cadastro simplesmente não funciona (título do card, cliente do
          card e nome do cliente). O resto é decisão sua.
        </Alert>

        <Alert variant="info" rail className="mb-4" data-testid="aviso-campos-rapido">
          Com a régua atual, o <strong>Cadastro rápido</strong> do funil mostra{' '}
          <strong>{camposDoRapido}</strong> campo(s) obrigatório(s). Cada campo exigido de Cliente
          (ou de todos) entra no fluxo do vendedor — exigir demais faz o rápido deixar de ser
          rápido.
        </Alert>

        {SECOES_INVENTARIO.map((secao) => (
          <Card key={secao.etiqueta || 'geral'} className="mb-6 min-w-0">
            <details open={secao.etiqueta === ''}>
              <summary className="cursor-pointer list-none px-5 pt-5 pb-1 select-none">
                <span className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
                  {secao.titulo}
                </span>
                <span className="ml-2 text-xs text-ink-3 dark:text-ink-dark-3">
                  {
                    secao.campos.filter(
                      (f) =>
                        findEntry(clientRows, '_global', 'client', f.fieldName, secao.etiqueta)
                          ?.required,
                    ).length
                  }{' '}
                  exigido(s)
                </span>
              </summary>
              <RequirementTable
                title=""
                subtitle={secao.descricao}
                loading={loadingClient}
                rows={secao.campos.map((f) => ({
                  ...f,
                  entry: findEntry(clientRows, '_global', 'client', f.fieldName, secao.etiqueta),
                }))}
                entityType="client"
                workflowForEntry="_global"
                etiqueta={secao.etiqueta}
                pendingKey={pending}
                onToggle={handleToggle}
              />
            </details>
          </Card>
        ))}

        <Card className="min-w-0">
          {WORKFLOWS.length > 1 && (
            <div className="px-5 pt-5">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Label htmlFor="workflow-select" className="text-xs">
                  Fluxo de trabalho:
                </Label>
                {WORKFLOWS.map((w, idx) => (
                  <button
                    key={w.slug}
                    id={idx === 0 ? 'workflow-select' : undefined}
                    type="button"
                    onClick={() => setWorkflowSlug(w.slug)}
                    className={`rounded-pill border px-3 py-1.5 text-xs transition-colors ${
                      workflowSlug === w.slug
                        ? 'border-red bg-red text-white'
                        : 'border-line bg-paper-surface text-ink-2 hover:border-red dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-2'
                    }`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <RequirementTable
            title="Card do funil"
            subtitle={`Vale pros cards do fluxo ${fluxoAtual}.`}
            loading={loadingCard}
            rows={cardConfig}
            entityType="card"
            workflowForEntry={workflowSlug}
            pendingKey={pending}
            onToggle={handleToggle}
          />
        </Card>
      </Secao>

      {/* Bloco D + P1: expediente/feriados e o prazo pra iniciar projeto — as
          duas configs são "tempo" e agora moram juntas. Âncora #sistema mantida
          (o menu admin linka /admin/configuracoes#financeiro e #sistema). */}
      <Secao
        id="sistema"
        className="mt-10"
        titulo="Expediente e prazos"
        descricao="Quando a empresa está aberta e quanto tempo a equipe tem pra começar o trabalho. É daqui que sai a conta de dias úteis do sistema inteiro."
      >
        <HorarioFuncionamentoConfigCard />
        {projetosV2 && <ProjetoConfigCard />}
        {projetosV2 && <CronogramaSemAtualizarCard />}
      </Secao>

      {/* I-2 — segurança do login. Seção própria: não é "expediente" nem
          "financeiro", e a notificação de bloqueio repetido linka direto pra
          âncora #seguranca-login (o card carrega o id). Só admin chega aqui —
          a rota inteira já vive sob o AdminLayout, e o endpoint por trás exige
          requireAdmin de novo (a tela nunca é o porteiro). */}
      <Secao
        id="seguranca"
        className="mt-10"
        titulo="Segurança do acesso"
        descricao="Quantas tentativas de senha o sistema aceita antes de travar um acesso, e quem está travado neste momento."
      >
        <SegurancaLoginConfigCard />
      </Secao>

      {/* R4 L2: seção Financeiro (Contas + Metas + Financiamento + Relatórios). */}
      <Secao
        id="financeiro"
        className="mt-10"
        titulo="Financeiro"
        descricao="Quais contas entram nos números, as metas do mês, o que é financiamento (e por isso não conta como lucro) e o envio dos relatórios."
      >
        <div data-testid="config-financeiro-section" className="min-w-0">
          <FinanceiroContasConfigCard />
          <FinanceiroMetasConfigCard />
          <FinanceiroFinanciamentoConfigCard />
          <FinanceiroRelatoriosConfigCard />
        </div>
      </Secao>
    </div>
  );
}
